import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { formatCurrency } from '@utils/tools/formatCurrenyToInteger';
import { VERIFICATION } from '../constants';
import verificationsHelper from '../helpers/verificationsHelper';

const StyledIncomeTable = styled.div``;

const StyledTable = styled.table`
    border: none;
    margin-top: 10px;
    font-size: 14px;
    width: 100%;
`;

const StyledHeader = styled.thead``;

const StyledHeaderRow = styled.tr``;

const StyledHeading = styled.th<{ width?: number }>`
    width: ${({ width }) => width}%;
    padding: 8px;
    font-weight: 600;
    max-width: ${({ width }) => width}%;
    min-width: ${({ width }) => width}%;
`;

const StyledColumn = styled.td<{ width?: number; fontWeight?: number; showBorder?: boolean; hidePadding?: boolean }>`
    width: ${({ width }) => width}%;
    padding: ${({ hidePadding = false }) => (hidePadding ? '0px 0px 0px 8px' : '8px')};
    overflow-wrap: anywhere;
    max-width: ${({ width }) => width}%;
    min-width: ${({ width }) => width}%;
    font-weight: ${({ fontWeight }) => fontWeight};
    border-top: ${({ showBorder }) => (showBorder ? '0.5px solid' : 'none')};

    .trashIcon {
        color: ${({ theme }) => theme.dangerMain};
        cursor: pointer;
    }
`;

const StyledLabelsColumn = styled.td<{ width?: number; hidePadding?: boolean }>`
    width: ${({ width }) => width}%;
    padding: ${({ hidePadding = false }) => (hidePadding ? '0px 8px 0px 0px' : '8px')};
    text-align: right;
    font-weight: 600;
    white-space: nowrap;
    @media (max-width: 639px) {
        white-space: normal;
    }
`;

const StyledBody = styled.tbody``;

const VerificationsIncomeDocumentsTable = ({
    id,
    incomeDocumentData,
    householdIncome,
    deleteHandler,
    documentCategoryDetailList,
}) => {
    const { calculateTotalAmount } = verificationsHelper();
    const totalAmount = calculateTotalAmount(incomeDocumentData);
    const intl = useIntl();
    const remainingAmount = householdIncome - totalAmount;

    return (
        <StyledIncomeTable data-testid={id}>
            <StyledTable>
                <StyledHeader>
                    <StyledHeaderRow>
                        <StyledHeading width={40}>
                            <FormattedMessage id="common.documentType" />
                        </StyledHeading>
                        <StyledHeading width={40}>
                            <FormattedMessage id="common.documentName" />
                        </StyledHeading>
                        <StyledHeading width={20}>
                            <FormattedMessage id="common.amount" />
                        </StyledHeading>
                        <StyledHeading width={10}></StyledHeading>
                    </StyledHeaderRow>
                </StyledHeader>
                <StyledBody>
                    {incomeDocumentData.map((data, index) => (
                        <StyledHeaderRow key={index}>
                            <StyledColumn width={40}>{data?.documentType}</StyledColumn>
                            <StyledColumn width={40}>{data?.documentName}</StyledColumn>
                            <StyledColumn width={20}>{formatCurrency(data?.amount)}</StyledColumn>
                            <StyledColumn width={10}>
                                <FontAwesomeIcon
                                    icon={faTrashAlt}
                                    title={intl.formatMessage({ id: 'dmi.button.delete.file' })}
                                    key={index}
                                    className="margin-r-10 trashIcon"
                                    onClick={() => deleteHandler(index)}
                                />
                            </StyledColumn>
                        </StyledHeaderRow>
                    ))}
                    <ConditionalRendering isTrue={documentCategoryDetailList.length === 0}>
                        <StyledHeaderRow>
                            <StyledLabelsColumn width={80} colSpan={2}>
                                <FormattedMessage id="dmi.applicantVerifications.incometable.proofLabel" />
                            </StyledLabelsColumn>
                            <StyledColumn width={20} fontWeight={600} showBorder={true}>
                                {formatCurrency(totalAmount)}
                            </StyledColumn>
                            <StyledColumn width={10}></StyledColumn>
                        </StyledHeaderRow>
                        <StyledHeaderRow>
                            <StyledLabelsColumn width={80} hidePadding={true} colSpan={2}>
                                <FormattedMessage id="dmi.applicantVerifications.incometable.statedIncomeLabel" />
                            </StyledLabelsColumn>
                            <StyledColumn width={20} fontWeight={600} hidePadding={true}>
                                {formatCurrency(householdIncome)}
                            </StyledColumn>
                            <StyledColumn width={10} hidePadding={true}></StyledColumn>
                        </StyledHeaderRow>
                        <StyledHeaderRow>
                            <StyledLabelsColumn width={80} hidePadding={true} colSpan={2}>
                                <FormattedMessage id="dmi.applicantVerifications.incometable.additionalIncomeProofLabel" />
                            </StyledLabelsColumn>
                            <StyledColumn width={20} fontWeight={600} hidePadding={true}>
                                {remainingAmount < 0 ? VERIFICATION.ZERO_DOLLARS : formatCurrency(remainingAmount)}
                            </StyledColumn>
                            <StyledColumn width={10} hidePadding={true}></StyledColumn>
                        </StyledHeaderRow>
                    </ConditionalRendering>
                </StyledBody>
            </StyledTable>
        </StyledIncomeTable>
    );
};

export default VerificationsIncomeDocumentsTable;
